import { Provider } from '@angular/core';
import { FINANCIAL_RECORDS_LIST_COMPONENT } from '@celerofinancas/common-domain';
import { CDS_INPUT_WRAPPER_CONFIG, provideCdsSnackbar } from '@celerofinancas/design-system';
import {
  BfmFinancialRecordsListComponent,
  FINANCIAL_RECORD_AMOUNT_FORM_CONFIGS,
  FINANCIAL_RECORD_AMOUNT_FORM_DEFAULT_CONFIGS,
  FINANCIAL_RECORD_AMOUNT_FORM_FIELDS,
  FINANCIAL_RECORD_AMOUNT_LIST_CONFIGS,
  FINANCIAL_RECORD_AMOUNT_LIST_DEFAULT_CONFIGS,
  FINANCIAL_RECORD_DETAILS_CONFIGS,
  FINANCIAL_RECORD_DETAILS_DEFAULT_CONFIGS,
  FINANCIAL_RECORD_DETAILS_DOCUMENTS_CONFIGS,
  FINANCIAL_RECORD_DETAILS_DOCUMENTS_DEFAULT_CONFIGS,
  FINANCIAL_RECORD_DETAILS_DOCUMENTS_LIST_COMPONENT,
  FINANCIAL_RECORD_PAYMENT_INFO_CONFIGS,
  FINANCIAL_RECORD_PAYMENT_INFO_FIELDS,
} from '@celerofinancas/ui-cash-flow';
import { ENABLE_COLOR_INPUT_CONFIG, SOURCE_IMAGE_DIRECTIVE_CONFIG } from '@celerofinancas/ui-directives';
import { HELP_CENTER_CONFIGURATION } from '@celerofinancas/ui-environments';
import { MODAL_COMPONENT_EXTERNAL } from '@celerofinancas/ui-modals';
import { environment } from '@environments/environment';

import { DocumentsListComponent } from '../shared/documents-list/documents-list.component';
import { ModalWrapperComponent } from '../shared/modal/modal-wrapper.component';
import { DASHBOARD_TOKENS } from './dashboard.tokens';
import { LIST_HEADER_BALANCE_TOKENS } from './list-header.tokens';

/**
 * Token providers for the custom layer — the equivalent of `configs/tokens.ts` in
 * `serasa-cl-webcomponents`.
 */
export const CL_TOKEN_PROVIDERS: Provider[] = [
  /**
   * Balance resolution for every balance display of the client interface — see
   * `list-header.tokens.ts` for the rule order and why the chain is pinned.
   */
  ...LIST_HEADER_BALANCE_TOKENS,

  /**
   * Dashboard blocks — see `dashboard.tokens.ts` for why this is here and not on the
   * screen that renders them.
   */
  ...DASHBOARD_TOKENS,
  /**
   * D10: inputs follow the GetNet design system layout, with the label **inside** the
   * field — small at the top when the field has a value or focus, in the place of the
   * text when it is empty. That is the input the wireframe 25:97 draws.
   *
   * The card asks for it in the input component, valid for the whole platform, and not
   * field by field. The design system already has that layout: `cds-input-wrapper` turns
   * it on through this token, and the label moves between the two positions on its own
   * (`label.active`). So this is configuration, and the GetNet measurements go in the
   * theme (`_components.overrides.scss`).
   *
   * Same token `serasa-cl-webcomponents` sets, with the same value.
   */
  { provide: CDS_INPUT_WRAPPER_CONFIG, useValue: { floatingLabel: true } },
  /**
   * Item 7 ([86e33fgd7](https://app.clickup.com/t/86e33fgd7)): "Saldo anterior" and
   * "Saldo final" say whether the balance is positive or negative — green above zero,
   * red below it.
   *
   * The red was already there; the green was not. `colorFromValue`, the directive that
   * paints a value by its sign, accepts the colour of each side as an input, and
   * `ui-cash-flow` passes `positiveColor="c-text-medium"` — the neutral text — on every
   * balance it draws. So a positive balance came out the same colour as the label next
   * to it, which is what the item reports.
   *
   * This flag tells the directive to ignore the colours it is given and use its own:
   * `c-revenue` above zero, `c-expense` below it. It is the token the design system
   * offers for exactly this, so the fix is configuration and no package is touched —
   * the item was mapped as a package correction.
   *
   * ## Why turning it off for the whole application is safe
   *
   * The flag is global, so it is worth knowing what else it reaches: **nothing**.
   * Sweeping the packages this project renders, the only overrides of the directive's
   * colours are the 14 `positiveColor="c-text-medium"` of `ui-cash-flow`, all of them on
   * balances; no package overrides the negative colour anywhere. Measured on the
   * statement before the change: every negative balance already `c-expense` (#CC0000),
   * every positive one `c-text-medium` (#444444).
   */
  { provide: ENABLE_COLOR_INPUT_CONFIG, useValue: false },
  /**
   * Frame for modals opened through `ModalService` — without it the modal shows up
   * transparent over the screen, because the default `ModalComponent` in
   * `ui-modals` has no styling at all. See the `ModalWrapperComponent` docblock.
   *
   * Does not affect the `ui-cash-flow` modals (record detail, cash flow filters):
   * those are opened by `CdsModalService`, which does not read this token, and they
   * style their own `:host`.
   */
  {
    provide: MODAL_COMPONENT_EXTERNAL,
    useValue: ModalWrapperComponent,
  },
  /**
   * Required, not optional: `ListHeaderComponent` and `BfmAccountTotalComponent` do
   * `inject(HELP_CENTER_CONFIGURATION)` with no fallback, so the bank balances header
   * throws `NG0201` without this provider.
   *
   * Empty because GetNet has no help center to link to — the same value
   * `serasa-cl-webcomponents` and `caixa-cl-webcomponents` provide
   * (`configs/tokens.ts` in both).
   */
  { provide: HELP_CENTER_CONFIGURATION, useValue: {} },
  /**
   * "Detalhe do Registro" modal — what the record card
   * ([86e2v9afr](https://app.clickup.com/t/86e2v9afr)) asks to hide.
   *
   * `showEconomicGroupName: false` drops the company name printed above the record
   * title. In the real product the modal can be reached from a screen listing several
   * companies, so naming the owner earns its place; here there is exactly one company
   * and the shell already says whose data this is.
   */
  {
    provide: FINANCIAL_RECORD_DETAILS_CONFIGS,
    useValue: {
      ...FINANCIAL_RECORD_DETAILS_DEFAULT_CONFIGS,
      showEconomicGroupName: false,
      // "Ocultar lançamentos" toggles a section that always holds exactly one amount
      // in this dataset — a control whose only effect is hiding the thing the modal
      // was opened to show.
      showHiddenAmountsButton: false,
    },
  },
  /**
   * Payment info section of the detail modal — dates, method and bank account.
   *
   * Every field is `editable: false`, for two reasons that happen to agree.
   *
   * The card's editable list has none of them: it names the record name, the amount
   * name, the classification, the accrual period and the observations. Payment data is
   * not on it, and it should not be — the reports derive the balance curve from
   * `paid_at`, so an edited payment date would make the statement and the P&L disagree.
   *
   * And it is what makes "Sua conta" render at all. With `editable: true` the section
   * draws `cds-checking-account-dropdown`, which resolves its label inside
   * `combineLatest([this.control.valueChanges, this.filteredAccounts$])` — and
   * `valueChanges` does not replay the current value, so the patch the details service
   * applies when the record loads arrives before anyone is listening and the field
   * stays blank. That is what Carlos saw. Read-only takes the template's `@else`
   * branch, which reads the record directly and prints the account.
   */
  {
    provide: FINANCIAL_RECORD_PAYMENT_INFO_CONFIGS,
    useValue: {
      fields: [
        [
          { key: FINANCIAL_RECORD_PAYMENT_INFO_FIELDS.DUE_AT, order: 1, editable: false, label: 'Vencimento' },
          { key: FINANCIAL_RECORD_PAYMENT_INFO_FIELDS.PAID_AT, order: 2, editable: false, label: 'Pago em' },
          {
            key: FINANCIAL_RECORD_PAYMENT_INFO_FIELDS.COMPENSATION_DATE,
            order: 3,
            editable: false,
            label: 'Compensação',
          },
        ],
        [
          {
            key: FINANCIAL_RECORD_PAYMENT_INFO_FIELDS.PAYMENT_METHOD,
            order: 1,
            editable: false,
            label: 'Método de pagamento',
          },
          {
            key: FINANCIAL_RECORD_PAYMENT_INFO_FIELDS.CHECKING_ACCOUNT,
            order: 2,
            editable: false,
            label: 'Sua conta',
          },
        ],
      ],
    },
  },
  /**
   * Amount form inside the detail modal.
   *
   * ## What is hidden
   *
   * - `showApplyToAllCheckbox`: "Aplicar para todos" copies a field across every
   *   amount of the record. Each record here has a single amount, so the checkbox
   *   applies a value to itself.
   * - `showViewMoreAmountInfo`: the "ver mais informações do lançamento" link opens a
   *   drill-down the mock has no endpoint for.
   *
   * ## Which fields are editable
   *
   * Editable: amount name, classification, accrual period, observations and cost
   * center. The record name is governed by `allowRecordNameEdit` plus the
   * `financialRecordNameEdit` liberation, so it is not in this list.
   *
   * Read-only: the value, because the reports derive their numbers from the same
   * records and an edited value would make the statement and the P&L disagree; and the
   * third party, which is display-only by request.
   *
   * Absent: "Tipo de lançamento", dropped from the list rather than disabled.
   *
   * Worth knowing: none of this shows unless the persona holds
   * `LIBERATIONS.financialRecords` (6). `setDisabledStates` opens with
   * `if (!this.hasLiberation) { group.disable(); return; }`, and the whole form goes
   * read-only regardless of what is configured here.
   */
  {
    provide: FINANCIAL_RECORD_AMOUNT_FORM_CONFIGS,
    useValue: {
      ...FINANCIAL_RECORD_AMOUNT_FORM_DEFAULT_CONFIGS,
      showApplyToAllCheckbox: false,
      showViewMoreAmountInfo: false,
      fields: [
        { key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.AMOUNT, order: 1, label: 'Valor do lançamento', editable: false },
        {
          key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.DESCRIPTION,
          order: 2,
          label: 'Nome do lançamento',
          editable: true,
        },
        {
          key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.CLASSIFICATION,
          order: 3,
          label: 'Classificação',
          editable: true,
        },
        // Item 27: a ordem pedida é competência e centro de custos lado a lado, depois
        // terceiro, e observações no fim. Antes o terceiro fechava a lista e observações
        // vinha no meio, separando os dois campos que o item quer juntos.
        //
        // O `order` decide a sequência; o lado a lado vem do próprio pacote, que dispõe os
        // campos num grid de duas colunas e emparelha vizinhos na ordem.
        {
          key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.ACCRUAL_BASIS,
          order: 4,
          label: 'Período de competência',
          editable: true,
        },
        // "Tipo de lançamento" is out of the list entirely, not merely read-only: the
        // dataset has no amount typing to show, so the field could only ever print "-".
        { key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.COST_CENTER, order: 5, label: 'Centro de custos', editable: true },
        // Third party is display-only by request: the block prints the name, the CNPJ
        // and the bank information, and none of it is the demo's to change.
        { key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.COMPANY_RELATED, order: 6, label: 'Terceiro', editable: false },
        {
          key: FINANCIAL_RECORD_AMOUNT_FORM_FIELDS.OBSERVATION,
          order: 7,
          label: 'Observações',
          editable: true,
        },
      ],
    },
  },
  /**
   * Without this provider the empty-state illustrations break.
   *
   * The `SourceImageDirective` from `ui-directives` rewrites the asset path by
   * inserting palette and/or theme segments:
   *
   * ```js
   * segments = [usePaletteOnPath ? palette : '', useThemeOnPath ? colorTheme : '']
   *   .filter(Boolean).join('/')
   * parsedSrc = path.replace(/(assets\/(core\/)?(common\/)?)/g, `$1${segments}/`)
   * ```
   *
   * `palette` comes from `environment.colorPalette`, which does not exist here — so
   * with the package default (`usePaletteOnPath: true`) the segment comes out empty
   * and the requested path is `assets/common/shared/no-content.svg`, which does not
   * exist. The directive walks its fallback hierarchy and in the end **throws**
   * `Invalid asset path`, blowing up the screen's change detection.
   *
   * Same configuration as Serasa: theme only in the path, resolving to
   * `assets/common/light/shared/…`, which is where the files are. `assetsUrl` comes
   * from `cdnUrl` (empty in dev, so the path stays relative).
   *
   * Both illustrations (`no-content.svg`, `error.svg`) were copied from Serasa —
   * they are generic, unbranded. The GetNet design system has its own family
   * (`Feedback/*` in the Samba Illustrations), which lands once we have Figma
   * access; it is on the design pending list.
   */
  {
    provide: SOURCE_IMAGE_DIRECTIVE_CONFIG,
    useValue: {
      useThemeOnPath: true,
      usePaletteOnPath: false,
      assetsUrl: environment.cdnUrl,
    },
  },
  /**
   * Entry list component, injected by token.
   *
   * The "DFM por Categorias" tab instantiates this list at runtime
   * (`container.createComponent(this.financialRecordsListComponent)`) to show the
   * entries when a category is opened. The token is **mandatory**: without it the
   * tab dies with `NG0201: No provider found for InjectionToken
   * FinancialRecordsListComponent` and renders nothing — which was exactly the
   * empty-tab symptom.
   *
   * The token comes from `common-domain` and the component from `ui-cash-flow`,
   * which entered the repo because of this (the same package the smart statement
   * needs). Pinned at 6.0.0, the same as Serasa, which runs with the same
   * `ui-reports` 6.0.0 and `common-domain` 5.0.1.
   */
  {
    provide: FINANCIAL_RECORDS_LIST_COMPONENT,
    useValue: BfmFinancialRecordsListComponent,
  },
  /**
   * "Detalhe do Registro" modal without the add-entry buttons — rule 6 of card
   * [86e2v9afr](https://app.clickup.com/t/86e2v9afr).
   *
   * `showUploadButtons: false` removes the whole `new-amounts-actions` block from
   * `amount-list`, which holds both the spreadsheet upload and the "add entry"
   * button (the plus icon).
   *
   * `showSelectCheckbox: false` comes along because selection exists for the bulk
   * actions, and the block's bulk action is **"Transformar em registro"** — which
   * creates new records. Same intent as rule 6: if adding an entry is out, creating
   * a record from an entry is out too.
   */
  {
    provide: FINANCIAL_RECORD_AMOUNT_LIST_CONFIGS,
    useValue: {
      ...FINANCIAL_RECORD_AMOUNT_LIST_DEFAULT_CONFIGS,
      showUploadButtons: false,
      showSelectCheckbox: false,
    },
  },
  /**
   * Documents for viewing only — rule 7 of the same card, which asks for Serasa's
   * pattern: *"the only option is to view the attached document"*.
   *
   * `showDocumentDataForm: false` drops the document data form (which would allow
   * editing), and `showReadOCRButton: false` drops OCR reading, a processing action
   * rather than a viewing one.
   *
   * The list itself is ours, below — see `DocumentsListComponent`.
   */
  {
    provide: FINANCIAL_RECORD_DETAILS_DOCUMENTS_CONFIGS,
    useValue: {
      ...FINANCIAL_RECORD_DETAILS_DOCUMENTS_DEFAULT_CONFIGS,
      showDocumentDataForm: false,
      showReadOCRButton: false,
      /**
       * Where the PDF viewer finds its worker and fonts. The package default is the bare
       * `'assets'`, relative — inside the client's portal that resolves against the
       * portal's origin. The files are copied into the bundle's `/assets/` by
       * `angular.json`; this points at them wherever the bundle is served, the same
       * value Serasa uses.
       *
       * Neither was set before, and nothing showed it: no document could be opened, so
       * the viewer never ran. Once the mock attached an openable document, it came up
       * blank.
       */
      pdfAssetsFolder: `${environment.cdnUrl}/assets`,
    },
  },
  /**
   * D8: the documents list of the wireframe 25:97 — an icon with "Adicionar", and each
   * attached document as a file icon with its name — with no delete, per rule 7. The
   * design system list writes its label and its thumbnail into the template, so the list
   * is replaced, the way Serasa does with `CustomDocumentsListComponent`.
   *
   * An earlier note here said configuration was enough because no record had a document.
   * The mock does attach one now, and the note itself said what to do when that happened.
   */
  {
    provide: FINANCIAL_RECORD_DETAILS_DOCUMENTS_LIST_COMPONENT,
    useValue: DocumentsListComponent,
  },
  /**
   * The design system ships no snackbar of its own: its components inject this token
   * and show nothing while nothing provides it. `CdsSnackbarService` already satisfies
   * the contract, so `useExisting` binds both to the same root instance.
   */
  provideCdsSnackbar(),
];
